import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import { useToast } from "../../context/ToastContext";
import "./RecruitingDashboard.css";
import {
  Container,
  Paper,
  Group,
  Stack,
  Title,
  Text,
  Button,
  Avatar,
  Grid,
  Loader,
  ActionIcon,
  Center,
  Badge,
  Anchor,
  Divider,
  Menu,
} from "@mantine/core";
import {
  IconChevronLeft,
  IconUser,
  IconPhone,
  IconCalendar,
  IconFolderOpen,
  IconDotsVertical,
  IconFolderPlus,
  IconFileText,
} from "@tabler/icons-react";
import PageContent from "../shared/PageContent/PageContent";
import ManageDocumentsModal from "./ManageDocumentsModal";
import AddToListModal from "./AddToListModal";
import { handleGenericError } from "../../utils/errorHandler";

function ListViewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { getVerifiedJWT, user } = useAuth();
  const { showError, showSuccess } = useToast();
  const [lists, setLists] = useState([]);
  const [isListsLoading, setIsListsLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
  const [isAddToListModalOpen, setIsAddToListModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const listId = searchParams.get("listId");
  const listName = searchParams.get("listName");

  const recruitingTabs = [
    { label: "Dashboard", path: "/recruiting" },
    { label: "Pipeline", path: "/recruiting/pipeline" },
    { label: "Lists", path: "/recruiting/list" },
    { label: "Documents", path: "/recruiting/documents" },
  ];

  const fetchRecruitingLists = async () => {
    setIsListsLoading(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v1/recruiting/lists"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setLists(result.data || []);
      }
    } catch (error) {
      handleGenericError(error, "Error loading recruiting lists");
    } finally {
      setIsListsLoading(false);
    }
  };

  // Fetch list leads
  const fetchListLeads = async () => {
    setIsLoading(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(
        getApiEndpoint(`/v1/recruiting/lists/${listId}/leads`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setLeads(result.data || []);
      } else {
        showError("Failed to load list");
      }
    } catch (error) {
      handleGenericError(error, "Error loading list");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data when component mounts
  useEffect(() => {
    if (listId) {
      fetchListLeads();
    } else {
      fetchRecruitingLists();
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId]);

  const handleBackToRecruiting = () => {
    navigate("/recruiting/list");
  };

  const handleViewLeadDetail = lead => {
    // Navigate to pipeline with this lead pre-selected
    navigate("/recruiting/pipeline", {
      state: {
        preloadedLeads: leads,
        selectedLeadId: lead.id,
      },
    });
  };

  const handleOpenList = list => {
    navigate(
      `/recruiting/list?listId=${list.id}&listName=${encodeURIComponent(list.name)}`
    );
  };

  const handleScheduleMeeting = (lead, e) => {
    e.stopPropagation();
    // Navigate to pipeline and trigger meeting modal
    navigate("/recruiting/pipeline", {
      state: {
        preloadedLeads: leads,
        selectedLeadId: lead.id,
        action: "schedule",
      },
    });
  };

  const handleLogContact = (lead, e) => {
    e.stopPropagation();
    // Navigate to pipeline and trigger contact modal
    navigate("/recruiting/pipeline", {
      state: {
        preloadedLeads: leads,
        selectedLeadId: lead.id,
        action: "contact",
      },
    });
  };

  const handleAddToList = (lead, e) => {
    e.stopPropagation();
    setSelectedLead(lead);
    setIsAddToListModalOpen(true);
  };

  const handleListsUpdated = () => {
    // Refresh the list to reflect changes
    fetchListLeads();
  };

  const handleManageDocuments = (lead, e) => {
    e.stopPropagation();
    setSelectedLead(lead);
    setIsDocumentsModalOpen(true);
  };

  const handleDocumentAction = async (action, lead) => {
    const jwt = await getVerifiedJWT();
    if (!jwt) return;

    try {
      switch (action) {
        case "sendESignature":
          // Navigate to pipeline to trigger eSignature modal
          navigate("/recruiting/pipeline", {
            state: {
              preloadedLeads: leads,
              selectedLeadId: lead.id,
              action: "sendDocs",
            },
          });
          break;

        case "markDocsSent":
          // Update lead status to "Offers Sent"
          const docsSentResponse = await fetch(
            getApiEndpoint(`/v1/recruiting/leads/${lead.id}/status`),
            {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${jwt}`,
              },
              body: JSON.stringify({ status: "Offers Sent" }),
            }
          );

          if (docsSentResponse.ok) {
            showSuccess(`${lead.name} moved to Offers Sent`);
            fetchListLeads(); // Refresh the list
          } else {
            showError("Failed to update status");
          }
          break;

        case "markSigned":
          // Update lead status to "Signed"
          const signedResponse = await fetch(
            getApiEndpoint(`/v1/recruiting/leads/${lead.id}/status`),
            {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${jwt}`,
              },
              body: JSON.stringify({ status: "Signed" }),
            }
          );

          if (signedResponse.ok) {
            showSuccess(`${lead.name} marked as signed!`);
            fetchListLeads(); // Refresh the list
          } else {
            showError("Failed to update status");
          }
          break;

        default:
          break;
      }
    } catch (error) {
      handleGenericError(error, "An error occurred");
    }
  };

  // Check if current user is the recruiter
  const isCurrentUserRecruiter = lead => {
    const currentUserName = `${user?.firstName} ${user?.lastName}`.trim();
    return lead.recruiter_name === currentUserName;
  };

  return (
    <PageContent
      customHeader={
        <div className="recruiting-page-custom-header">
          <div className="recruiting-page-header-left">
            <h1 className="recruiting-page-main-title">Recruiting</h1>
            <div className="recruiting-page-nav-tabs">
              {recruitingTabs.map(tab => (
                <button
                  key={tab.path}
                  className={`recruiting-page-nav-tab ${
                    location.pathname === tab.path ? "active" : ""
                  }`}
                  onClick={() => navigate(tab.path)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      }
    >
      <Container size="xl" px="md">
        {!listId && (
          <Paper p="xl" mb="xl" withBorder>
            <Group justify="space-between" align="flex-start">
              <Group align="center">
                <Avatar size={48} radius="md" color="blue" variant="filled">
                  <IconFolderOpen size={24} />
                </Avatar>
                <Stack gap="xs">
                  <Title order={1} size="h2">
                    My Recruiting Lists
                  </Title>
                  <Text c="dimmed" size="sm" fw={500}>
                    {isListsLoading
                      ? "Loading..."
                      : `${lists.length} ${lists.length === 1 ? "list" : "lists"}`}
                  </Text>
                </Stack>
              </Group>
            </Group>
          </Paper>
        )}

        {listId && (
          <Paper p="xl" mb="xl" withBorder>
            <Group justify="space-between" align="flex-start">
              <Group align="center">
                <Avatar size={48} radius="md" color="blue" variant="filled">
                  <IconFolderOpen size={24} />
                </Avatar>
                <Stack gap="xs">
                  <Title order={1} size="h2">
                    {listName || "List View"}
                  </Title>
                  <Text c="dimmed" size="sm" fw={500}>
                    {isLoading
                      ? "Loading..."
                      : `${leads.length} ${leads.length === 1 ? "lead" : "leads"} in this list`}
                  </Text>
                </Stack>
              </Group>

              <Button
                leftSection={<IconChevronLeft size={16} />}
                onClick={handleBackToRecruiting}
                variant="filled"
              >
                Back to Lists
              </Button>
            </Group>
          </Paper>
        )}

        {/* Content */}
        {!listId && isListsLoading ? (
          <Center py="xl">
            <Stack align="center" gap="md">
              <Loader color="blue" size="lg" />
              <Text c="dimmed">Loading lists...</Text>
            </Stack>
          </Center>
        ) : !listId && lists.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="md" ta="center">
              <Avatar size={80} color="blue" variant="light">
                <IconFolderOpen size={40} />
              </Avatar>
              <Title order={3}>No Lists Yet</Title>
              <Text c="dimmed" maw={400}>
                Create a list from the Recruiting dashboard and it will appear
                here.
              </Text>
              <Button onClick={() => navigate("/recruiting")} variant="filled">
                Go to Recruiting Dashboard
              </Button>
            </Stack>
          </Center>
        ) : !listId ? (
          <Grid gutter="lg">
            {lists.map((list, index) => (
              <Grid.Col
                key={list.id || index}
                span={{ base: 12, sm: 6, lg: 4 }}
              >
                <Paper
                  p="lg"
                  withBorder
                  radius="md"
                  h="100%"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleOpenList(list)}
                >
                  <Stack gap="sm">
                    <Group justify="space-between" align="center">
                      <Group align="center" gap="sm">
                        <Avatar
                          size={36}
                          radius="md"
                          color="blue"
                          variant="light"
                        >
                          <IconFolderOpen size={18} />
                        </Avatar>
                        <Text fw={600} size="sm" lineClamp={1}>
                          {list.name}
                        </Text>
                      </Group>
                      <Badge variant="light" color="blue">
                        {list.lead_count || 0}
                      </Badge>
                    </Group>
                    <Text c="dimmed" size="xs">
                      {list.is_default || list.is_system_list
                        ? "Default list"
                        : `Updated ${new Date(list.updated_at).toLocaleDateString()}`}
                    </Text>
                  </Stack>
                </Paper>
              </Grid.Col>
            ))}
          </Grid>
        ) : isLoading ? (
          <Center py="xl">
            <Stack align="center" gap="md">
              <Loader color="blue" size="lg" />
              <Text c="dimmed">Loading leads...</Text>
            </Stack>
          </Center>
        ) : leads.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="md" ta="center">
              <Avatar size={80} color="blue" variant="light">
                <IconFolderOpen size={40} />
              </Avatar>
              <Title order={3}>No Leads in This List</Title>
              <Text c="dimmed" maw={400}>
                This list is currently empty. Add leads from the recruiting
                dashboard.
              </Text>
              <Button
                leftSection={<IconChevronLeft size={16} />}
                onClick={handleBackToRecruiting}
                variant="filled"
              >
                Back to Recruiting Dashboard
              </Button>
            </Stack>
          </Center>
        ) : (
          <Grid gutter="lg">
            {leads.map((lead, index) => (
              <Grid.Col
                key={lead.id || index}
                span={{ base: 12, sm: 6, lg: 4 }}
              >
                <Paper
                  p="lg"
                  withBorder
                  radius="md"
                  h="100%"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleViewLeadDetail(lead)}
                >
                  <Stack gap="md">
                    <Group justify="space-between" align="flex-start">
                      <Group align="center">
                        <Avatar
                          size={40}
                          radius="xl"
                          color="blue"
                          variant="filled"
                        >
                          <IconUser size={20} />
                        </Avatar>
                        <Stack gap={2}>
                          <Text fw={600} size="sm" lineClamp={1}>
                            {lead.name || `Unnamed Lead ${index + 1}`}
                          </Text>
                          <Badge
                            size="xs"
                            color={
                              lead.status === "Signed"
                                ? "green"
                                : lead.status === "Offers Sent"
                                  ? "orange"
                                  : "blue"
                            }
                            variant="light"
                          >
                            {lead.status || "Lead"}
                          </Badge>
                        </Stack>
                      </Group>

                      {isCurrentUserRecruiter(lead) && (
                        <Menu position="bottom-end" shadow="md">
                          <Menu.Target>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              size="sm"
                              onClick={e => e.stopPropagation()}
                            >
                              <IconDotsVertical size={16} />
                            </ActionIcon>
                          </Menu.Target>

                          <Menu.Dropdown>
                            <Menu.Item
                              leftSection={<IconPhone size={14} />}
                              onClick={e => handleLogContact(lead, e)}
                            >
                              Log Contact
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconCalendar size={14} />}
                              onClick={e => handleScheduleMeeting(lead, e)}
                            >
                              Schedule Meeting
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconFolderPlus size={14} />}
                              onClick={e => handleAddToList(lead, e)}
                            >
                              Add to List
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconFileText size={14} />}
                              onClick={e => handleManageDocuments(lead, e)}
                            >
                              Manage Documents
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      )}
                    </Group>

                    <Stack gap="xs">
                      {lead.email && (
                        <Group gap="xs">
                          <Text size="xs" c="dimmed" w={70}>
                            Email:
                          </Text>
                          <Anchor
                            href={`mailto:${lead.email}`}
                            size="xs"
                            lineClamp={1}
                            onClick={e => e.stopPropagation()}
                          >
                            {lead.email}
                          </Anchor>
                        </Group>
                      )}

                      {(lead.phone || lead.phone_number) && (
                        <Group gap="xs">
                          <Text size="xs" c="dimmed" w={70}>
                            Phone:
                          </Text>
                          <Text size="xs" lineClamp={1}>
                            {lead.phone_number || lead.phone}
                          </Text>
                        </Group>
                      )}

                      {lead.instagram_username && (
                        <Group gap="xs">
                          <Text size="xs" c="dimmed" w={70}>
                            Instagram:
                          </Text>
                          <Anchor
                            href={`https://instagram.com/${lead.instagram_username}`}
                            target="_blank"
                            size="xs"
                            lineClamp={1}
                            onClick={e => e.stopPropagation()}
                          >
                            @{lead.instagram_username}
                          </Anchor>
                        </Group>
                      )}

                      {lead.linkedin_profile && (
                        <Group gap="xs">
                          <Text size="xs" c="dimmed" w={70}>
                            LinkedIn:
                          </Text>
                          <Anchor
                            href={lead.linkedin_profile}
                            target="_blank"
                            size="xs"
                            lineClamp={1}
                            onClick={e => e.stopPropagation()}
                          >
                            View Profile
                          </Anchor>
                        </Group>
                      )}

                      {lead.recruiter_name && (
                        <Group gap="xs">
                          <Text size="xs" c="dimmed" w={70}>
                            Recruiter:
                          </Text>
                          <Text size="xs" lineClamp={1} fw={500}>
                            {lead.recruiter_name}
                          </Text>
                        </Group>
                      )}

                      {lead.last_contact_date && (
                        <Group gap="xs">
                          <Text size="xs" c="dimmed" w={70}>
                            Last contact:
                          </Text>
                          <Text size="xs" fs="italic">
                            {new Date(
                              lead.last_contact_date
                            ).toLocaleDateString()}
                          </Text>
                        </Group>
                      )}

                      {lead.last_meeting_date && (
                        <Group gap="xs">
                          <Text size="xs" c="dimmed" w={70}>
                            Last meeting:
                          </Text>
                          <Text size="xs" fs="italic">
                            {new Date(
                              lead.last_meeting_date
                            ).toLocaleDateString()}
                          </Text>
                        </Group>
                      )}
                    </Stack>

                    {isCurrentUserRecruiter(lead) && (
                      <>
                        <Divider />

                        <Group gap="xs" grow>
                          <Button
                            variant="light"
                            size="xs"
                            leftSection={<IconPhone size={14} />}
                            onClick={e => handleLogContact(lead, e)}
                            style={{
                              backgroundColor: "#e0f7f4",
                              color: "#14b8a6",
                            }}
                            styles={{
                              root: {
                                "&:hover": {
                                  backgroundColor: "#ccf3ed",
                                },
                              },
                            }}
                          >
                            Contact
                          </Button>
                          <Button
                            variant="light"
                            size="xs"
                            leftSection={<IconFolderPlus size={14} />}
                            onClick={e => handleAddToList(lead, e)}
                            style={{
                              backgroundColor: "#ccf3ed",
                              color: "#14b8a6",
                            }}
                            styles={{
                              root: {
                                "&:hover": {
                                  backgroundColor: "#b3efe5",
                                },
                              },
                            }}
                          >
                            Add to List
                          </Button>
                        </Group>
                      </>
                    )}
                  </Stack>
                </Paper>
              </Grid.Col>
            ))}
          </Grid>
        )}

        {/* Manage Documents Modal */}
        <ManageDocumentsModal
          isOpen={isDocumentsModalOpen}
          onClose={() => {
            setIsDocumentsModalOpen(false);
            setSelectedLead(null);
          }}
          recruit={selectedLead}
          onAction={handleDocumentAction}
        />

        {/* Add to List Modal */}
        <AddToListModal
          isOpen={isAddToListModalOpen}
          onClose={() => {
            setIsAddToListModalOpen(false);
            setSelectedLead(null);
          }}
          recruit={selectedLead}
          onSuccess={handleListsUpdated}
        />
      </Container>
    </PageContent>
  );
}

export default ListViewPage;
